import React, { useEffect, useState } from 'react';

import Icon from '../../../components/AppIcon';
import { homepageSliderService } from '../../../services/homepageSliderService';
import { storageService } from '../../../services/storageService';

const HomepageManager = () => {
	const [slides, setSlides] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editingSlide, setEditingSlide] = useState(null);
	const [showAddForm, setShowAddForm] = useState(false);
	const [formData, setFormData] = useState({
		title: '',
		subtitle: '',
		description: '',
		button_text: '',
		button_url: '',
		image_url: '',
		order_index: 0,
		is_active: true,
	});

	useEffect(() => {
		loadSlides();
	}, []);

	const loadSlides = async () => {
		try {
			setLoading(true);
			const { data, error } = await homepageSliderService.getSlides();
			if (error) {
				console.error('Error loading slides:', error);
				setSlides([]);
			} else {
				setSlides(data || []);
			}
		} catch (error) {
			console.error('Error loading slides:', error);
			setSlides([]);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			if (editingSlide) {
				// Update existing slide
				const updatedSlide = { ...editingSlide, ...formData };
				await homepageSliderService.updateSlide(editingSlide.id, updatedSlide);
				alert('Slide updated successfully!');
			} else {
				// Create new slide
				await homepageSliderService.createSlide(formData);
				alert('Slide created successfully!');
			}

			resetForm();
			loadSlides();
		} catch (error) {
			console.error('Error saving slide:', error);
			alert('Error saving slide: ' + error.message);
		}
	};

	const handleEdit = (slide) => {
		setEditingSlide(slide);
		setFormData(slide);
		setShowAddForm(true);
	};

	const handleDelete = async (slideId) => {
		if (!confirm('Are you sure you want to delete this slide?')) {
			return;
		}

		try {
			await homepageSliderService.deleteSlide(slideId);
			alert('Slide deleted successfully!');
			loadSlides();
		} catch (error) {
			console.error('Error deleting slide:', error);
			alert('Error deleting slide: ' + error.message);
		}
	};

	const resetForm = () => {
		setFormData({
			title: '',
			subtitle: '',
			description: '',
			button_text: '',
			button_url: '',
			image_url: '',
			order_index: 0,
			is_active: true,
		});
		setEditingSlide(null);
		setShowAddForm(false);
	};

	const handleImageUpload = async (file) => {
		try {
			const { data, error } = await storageService.uploadImage(file, 'images', 'homepage-slides/');

			if (error) {
				console.error('Upload error:', error);
				alert('Upload failed: ' + error.message);
				return;
			}

			if (data) {
				setFormData((prev) => ({ ...prev, image_url: data.publicUrl }));
				alert('Image uploaded successfully!');
			}
		} catch (error) {
			console.error('Upload error:', error);
			alert('Upload failed: ' + error.message);
		}
	};

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex items-center justify-between'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>Homepage Slides Manager</h1>
						<p className='text-gray-600'>Manage the hero slider content on your homepage</p>
					</div>
					<button
						onClick={() => setShowAddForm(true)}
						className='bg-[#800020] text-white px-4 py-2 rounded-md hover:bg-[#6b001a] transition-colors'
					>
						<Icon name='Plus' className='h-4 w-4 inline mr-2' />
						Add New Slide
					</button>
				</div>
			</div>

			{/* Add/Edit Form */}
			{showAddForm && (
				<div className='bg-white shadow rounded-lg p-6'>
					<h2 className='text-lg font-semibold text-gray-900 mb-4'>
						{editingSlide ? 'Edit Slide' : 'Add New Slide'}
					</h2>

					<form onSubmit={handleSubmit} className='space-y-4'>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Title</label>
								<input
									type='text'
									value={formData.title}
									onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									required
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Subtitle</label>
								<input
									type='text'
									value={formData.subtitle}
									onChange={(e) => setFormData((prev) => ({ ...prev, subtitle: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>Description</label>
							<textarea
								value={formData.description}
								onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
								rows={3}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
							/>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Button Text</label>
								<input
									type='text'
									value={formData.button_text}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, button_text: e.target.value }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Button URL</label>
								<input
									type='text'
									value={formData.button_url}
									onChange={(e) => setFormData((prev) => ({ ...prev, button_url: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Image URL</label>
								<input
									type='text'
									value={formData.image_url}
									onChange={(e) => setFormData((prev) => ({ ...prev, image_url: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='https://your-project.supabase.co/storage/v1/object/public/images/...'
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Upload Image</label>
								<input
									type='file'
									accept='image/*'
									onChange={(e) => {
										if (e.target.files[0]) {
											handleImageUpload(e.target.files[0]);
										}
									}}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Order Index</label>
								<input
									type='number'
									value={formData.order_index}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, order_index: parseInt(e.target.value) }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>

							<div className='flex items-center'>
								<input
									type='checkbox'
									id='is_active'
									checked={formData.is_active}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
									}
									className='h-4 w-4 text-[#800020] focus:ring-[#800020] border-gray-300 rounded'
								/>
								<label htmlFor='is_active' className='ml-2 block text-sm text-gray-900'>
									Active
								</label>
							</div>
						</div>

						<div className='flex justify-end space-x-3'>
							<button
								type='button'
								onClick={resetForm}
								className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors'
							>
								Cancel
							</button>
							<button
								type='submit'
								className='px-4 py-2 bg-[#800020] text-white rounded-md hover:bg-[#6b001a] transition-colors'
							>
								{editingSlide ? 'Update Slide' : 'Create Slide'}
							</button>
						</div>
					</form>
				</div>
			)}

			{/* Slides List */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Current Slides</h2>

				{loading ? (
					<div className='flex items-center justify-center py-8'>
						<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
						<span className='ml-2 text-gray-600'>Loading slides...</span>
					</div>
				) : !slides || !Array.isArray(slides) || slides.length === 0 ? (
					<div className='text-center py-8 text-gray-500'>
						<Icon name='Image' className='h-12 w-12 mx-auto mb-4 text-gray-300' />
						<p>No slides found</p>
					</div>
				) : (
					<div className='space-y-4'>
						{slides.map((slide) => (
							<div key={slide.id} className='border border-gray-200 rounded-lg p-4'>
								<div className='flex items-start justify-between'>
									<div className='flex-1'>
										<div className='flex items-center space-x-4'>
											<div className='w-24 h-16 bg-gray-100 rounded overflow-hidden'>
												{slide.image_url ? (
													<img
														src={slide.image_url}
														alt={slide.title}
														className='w-full h-full object-cover'
													/>
												) : (
													<div className='w-full h-full flex items-center justify-center text-gray-400'>
														<Icon name='Image' className='h-6 w-6' />
													</div>
												)}
											</div>
											<div className='flex-1'>
												<h3 className='font-medium text-gray-900'>{slide.title}</h3>
												<p className='text-sm text-gray-600'>{slide.subtitle}</p>
												<p className='text-xs text-gray-500 mt-1'>Order: {slide.order_index}</p>
												<div className='flex items-center mt-2'>
													<span
														className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
															slide.is_active
																? 'bg-green-100 text-green-800'
																: 'bg-red-100 text-red-800'
														}`}
													>
														{slide.is_active ? 'Active' : 'Inactive'}
													</span>
												</div>
											</div>
										</div>
									</div>
									<div className='flex space-x-2'>
										<button
											onClick={() => handleEdit(slide)}
											className='text-[#800020] hover:text-[#6b001a] transition-colors'
										>
											<Icon name='Edit' className='h-4 w-4' />
										</button>
										<button
											onClick={() => handleDelete(slide.id)}
											className='text-red-600 hover:text-red-700 transition-colors'
										>
											<Icon name='Trash2' className='h-4 w-4' />
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default HomepageManager;
